<template>
  <bm-dialog
    :label="$m.production_packaging_assembly_print_labels"
    @accept="submit"
    @cancel="cancel"
  >
    <v-container pa-0 fluid>
      <!-- Only ask for stock item if we don't have one -->
      <v-row v-if="!stockItem">
        <v-col>
          <bm-dropdown
            :value.sync="selectedItem"
            :t="$m.keys.enum_noun_item"
            :items="items"
            item-text="details.name"
            return-object
          />
        </v-col>
      </v-row>
      <!-- Only ask for a URN if we don't have one -->
      <div v-if="selectedItem">
        <v-row>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-text
              :value.sync="newBatchUrn"
              :t="$m.keys.assembly_batch_number"
              :rules="[$ruleSet.required()]"
              :disabled="!!batchUrn"
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-dropdown
              :value.sync="newBatchUrn"
              :t="$m.keys.assembly_batch_number"
              :items="itemStockBatches"
              item-text="urn"
              item-value="urn"
              allow-custom-value
            />
          </v-col>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-date-q
              :value.sync="newBestBefore"
              :t="$m.keys.assembly_best_before_date"
              clearable
            />
          </v-col>
          <v-col v-if="showReturnable || showNonReturnable" cols="12">
            <bm-date-q
              :value.sync="newPackagedOn"
              :t="$m.keys.container_label_packaged_on"
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-date-q
              :value.sync="newUseByDate"
              :t="$m.keys.stock_use_by_date"
              clearable
            />
          </v-col>
          <v-col v-if="showMaterialLabel" cols="12">
            <bm-date-q
              :value.sync="newCreationDate"
              :t="$m.keys.stock_creation_date"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            {{ $m.production_select_number_labels }}
          </v-col>
        </v-row>
        <v-row v-if="showNonReturnable">
          <v-col :cols="8">
            <bm-dropdown
              :value.sync="nonReturnableTemplateId"
              :items="nonReturnableTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number-q
              :value.sync="nonReturnableQuantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
              :default-value="null"
              placeholder="0"
              integral
            >
            </bm-number-q>
          </v-col>
        </v-row>
        <v-row v-if="showReturnable">
          <v-col :cols="8">
            <bm-dropdown
              :value.sync="returnableTemplateId"
              :items="returnableTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number-q
              :value.sync="returnableQuantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
              :default-value="null"
              placeholder="0"
              integral
            >
            </bm-number-q>
          </v-col>
        </v-row>
        <v-row v-if="showMaterialLabel">
          <v-col :cols="8">
            <bm-dropdown
              :value.sync="materialTemplateId"
              :items="materialLabelTemplates"
              :rules="[$ruleSet.required(false)]"
              :t="$m.keys.production_template"
              item-value="id"
              item-text="name"
            ></bm-dropdown>
          </v-col>
          <v-col :cols="4">
            <bm-number-q
              :value.sync="materialQuantity"
              :rules="[$ruleSet.notNegativeNumber()]"
              :t="$m.keys.production_label_qty"
              :default-value="null"
              placeholder="0"
              integral
            >
            </bm-number-q>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <template #controls-confirm-text>
      {{ $m.production_print_labels }}
    </template>
  </bm-dialog>
</template>

<script setup lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import {
  containerLabelService,
  LabelTemplateAndQuantity,
} from "@/app/services/generated/containerlabelService";
import type { DocumentTemplate } from "@/app/services/generated/documenttemplateService";
import { documentTemplateService } from "@/app/services/generated/documenttemplateService";
import { EnumDocumentType } from "@/app/services/generated/enums/enumDocumentType.generated";
import type Guid from "@/app/types/common/guid";
import { computed, onMounted, watch } from "vue";
import { withLoading } from "@/components/composables/componentTools";
import type { LocalDate } from "@js-joda/core";
import {
  initiateDownloadFromBytes,
  isNotNullOrUndefined,
  toGroupedGuidMap,
} from "@/app/apiutils";
import { EnumDocumentFileType } from "@/app/services/generated/enums/enumDocumentFileType.generated";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { StockBatch } from "@/app/services/generated/stockbatchService";
import { stockBatchService } from "@/app/services/generated/stockbatchService";
import { EnumYesNoEither } from "@/app/services/generated/enums/enumYesNoEither.generated";
import { itemDocumentService } from "@/app/services/generated/itemdocumentService";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { Item } from "@/app/services/generated/itemService";
import {
  itemBestBeforeDate,
  itemIsReturnable,
  itemIsTraceable,
  itemPackageType,
} from "@/business_logic/stock/itemUtils";
import { GuidMap } from "@/app/types/common/guidcollections";
import type { Maybe } from "@/components/utils/typeHelpers";
import { buildCache } from "@/app/caching/buildCache";
import { translateWithParams } from "@/plugins/i18n";

export interface PrintLabelsEvent {
  newBestBefore: LocalDate;
}

interface PrintLabelsDialogEvents extends DialogEvents<PrintLabelsEvent> {}
const emits = defineEmits<PrintLabelsDialogEvents>();

export interface PrintLabelsDialogProps {
  stockItem?: Item | null;
  rackedQuantity?: number;
  producedQuantity?: number;
  batchUrn?: string;
  bestBefore?: LocalDate | null;
  useByDate?: LocalDate;
  creationDate?: LocalDate;
  packagedOn?: LocalDate;
  processSubBatch?: string;
}

const props = defineProps<PrintLabelsDialogProps>();

const { accept, cancel } =
  useReactiveDialogScriptSetup<PrintLabelsEvent>(emits);

const cache = buildCache().withItems().withPackageTypes().build();
const allDocumentTemplates = makeRef<readonly DocumentTemplate[]>([]);

const items = makeRef<readonly Item[]>([]);
const selectedItem = makeRef(props.stockItem ?? null);

const returnableTemplateId = makeRef<Guid | null>(null);
const returnableQuantity = makeRef<number | null>(null);
const nonReturnableTemplateId = makeRef<Guid | null>(null);
const nonReturnableQuantity = makeRef<number | null>(null);
const materialTemplateId = makeRef<Guid | null>(null);
const materialQuantity = makeRef<number | null>(1);

const newBatchUrn = makeRef(props.batchUrn ?? "");
const newBestBefore = makeRef(props.bestBefore ?? null);
const newUseByDate = makeRef(props.useByDate);
const newCreationDate = makeRef(props.creationDate ?? tenantService.today);
const newPackagedOn = makeRef(props.packagedOn ?? tenantService.today);

const stockBatchesPerItem = makeRef<GuidMap<StockBatch[]>>(new GuidMap());

const selectedItemId = computed(() => selectedItem.value?.details.id);
const isMaterial = computed(
  () => selectedItem.value?.details.mainPurpose.isMaterial() ?? false
);
const itemStockBatches = computed(
  () => stockBatchesPerItem.value.get(selectedItemId.value) ?? []
);

const nonReturnableTemplates = computed(() =>
  allDocumentTemplates.value.filter((x) =>
    x.documentType.isNonReturnableLabel()
  )
);
const returnableTemplates = computed(() =>
  allDocumentTemplates.value.filter((x) => x.documentType.isReturnableLabel())
);
const materialLabelTemplates = computed(() =>
  allDocumentTemplates.value.filter((x) => x.documentType.isMaterialLabel())
);

const templateAndQuantity = (
  type: EnumDocumentType,
  templateId: Maybe<Guid>,
  quantity: Maybe<number>
) => {
  if (templateId && quantity) {
    const result = new LabelTemplateAndQuantity();
    result.labelDocumentType = type;
    result.labelDocumentTemplateId = templateId;
    result.quantity = quantity;
    return result;
  }
  return null;
};

const submit = withLoading(async () => {
  let document: Uint8Array | null = null;
  if (selectedItemId.value) {
    if (showMaterialLabel.value) {
      if (materialTemplateId.value && materialQuantity.value) {
        document = await itemDocumentService.generateMaterialLabel(
          selectedItemId.value!,
          EnumDocumentFileType.pdf,
          materialQuantity.value,
          newUseByDate.value ?? null,
          newCreationDate.value,
          newBatchUrn.value,
          materialTemplateId.value
        );
      }
    } else {
      const labels = [
        templateAndQuantity(
          EnumDocumentType.returnableLabel,
          returnableTemplateId.value,
          returnableQuantity.value
        ),
        templateAndQuantity(
          EnumDocumentType.nonReturnableLabel,
          nonReturnableTemplateId.value,
          nonReturnableQuantity.value
        ),
      ].filter(isNotNullOrUndefined);

      if (labels.length) {
        document = await containerLabelService.generateContainerLabels(
          selectedItemId.value!,
          newBatchUrn.value,
          newBestBefore.value,
          EnumDocumentFileType.pdf,
          newPackagedOn.value,
          props.processSubBatch ?? "",
          labels
        );
      }
    }
  }

  if (document) {
    const filename = translateWithParams("production_label_template_pdf", {
      key: newBatchUrn.value,
    }).toString();

    await initiateDownloadFromBytes(
      filename,
      "application/pdf",
      async () => document
    );

    accept({
      newBestBefore: newBestBefore.value!,
    });
  } else {
    // No labels selected = cancel
    cancel();
  }
});

const loadPrerequisites = withLoading(async () => {
  [allDocumentTemplates.value] = await Promise.all([
    documentTemplateService.getAllDocumentTemplates(),
    cache.update(),
  ]);

  const defaultNonReturnableTemplate = nonReturnableTemplates.value.find(
    (nrt) => nrt.isDocumentTypeDefault
  );

  const defaultReturnableTemplate = returnableTemplates.value.find(
    (rt) => rt.isDocumentTypeDefault
  );

  const defaultMaterialTemplate = materialLabelTemplates.value.find(
    (mt) => mt.isDocumentTypeDefault
  );

  const packageType = itemPackageType(selectedItem.value, cache);

  // If We have produced more than we have racked we take the Delta into the Non Returnables as a suggestion
  const produced = props.producedQuantity ?? 0;
  const racked = props.rackedQuantity ?? 0;
  if (packageType && produced) {
    if (packageType.isReturnable) {
      if (racked && racked < produced) {
        nonReturnableQuantity.value = produced - racked;
        returnableQuantity.value = racked;
      } else {
        returnableQuantity.value = produced;
      }
    } else if (packageType.isTraceable) {
      nonReturnableQuantity.value = produced;
    }
  }

  nonReturnableTemplateId.value =
    packageType?.defaultNonReturnableLabelDocumentTemplateId ??
    defaultNonReturnableTemplate?.id ??
    nonReturnableTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
    null;

  returnableTemplateId.value =
    packageType?.defaultReturnableLabelDocumentTemplateId ??
    defaultReturnableTemplate?.id ??
    returnableTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
    null;

  materialTemplateId.value =
    defaultMaterialTemplate?.id ??
    materialLabelTemplates.value.filter((x) => !x.isHidden)[0]?.id ??
    null;

  items.value = cache.items.all.filter(
    (item) =>
      item.details.mainPurpose.isMaterial() ||
      ((item.details.mainPurpose.isProduct() ||
        item.details.mainPurpose.isAutoAssembledItem()) &&
        (itemIsTraceable(item, cache) || itemIsReturnable(item, cache)))
  );

  stockBatchesPerItem.value = toGroupedGuidMap(
    await stockBatchService.getStockBatchesForItems(
      items.value.map((x) => x.details.id),
      EnumYesNoEither.no
    ),
    (x) => x.stockItemId
  );

  newBatchUrn.value = isMaterial.value
    ? itemStockBatches.value[0]?.urn
    : (props.batchUrn ?? "");

  evaluateBestBeforeDate();
});

const evaluateBestBeforeDate = () => {
  if (props.bestBefore) {
    newBestBefore.value = props.bestBefore;
  } else if (selectedItemId.value) {
    newBestBefore.value = itemBestBeforeDate(
      cache.items.getById(selectedItemId.value)
    );
  }
};

watch(selectedItem, () => evaluateBestBeforeDate());

const showNonReturnable = computed(
  () =>
    nonReturnableTemplates.value.length &&
    itemIsTraceable(selectedItem.value, cache)
);

const showReturnable = computed(
  () =>
    returnableTemplates.value.length &&
    itemIsReturnable(selectedItem.value, cache)
);

const showMaterialLabel = computed(
  () => materialLabelTemplates.value.length && isMaterial.value
);

onMounted(withLoading(loadPrerequisites));
</script>
